/**
 * Upload-new-version dialog rendered from the Versions tab.
 *
 * The project is already known: the caller passes its name + org, so we
 * don't need a name field or an org selector. The user picks the source
 * tool (KiCad / EasyEDA) and supplies the required netlist + BOM (plus
 * optional schematic / PCB) — users can legitimately switch tools
 * between versions.
 *
 * Optional `version_description` is an unstructured short note shown in
 * the timeline ("fixed VCC routing on the PSU rail"). Empty → no
 * description stored.
 */
import { useUploadProject } from "@features/projects/hooks/useProjects";
import { ToolUploadFields } from "@features/upload/ToolUploadFields";
import {
  type SlotFiles,
  type SlotKey,
  type SourceTool,
  buildBoardUploadFormData,
  getTool,
  requiredSlotsFilled,
} from "@features/upload/tools";
import { validateSlotFile } from "@features/upload/validate";
import CloseIcon from "@mui/icons-material/CloseRounded";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";

interface UploadVersionDialogProps {
  open: boolean;
  projectName: string;
  organisationId: string | null;
  /** Target board UUID — when set, the new version lands on this exact
   *  board (no name resolution), never a same-named board elsewhere. */
  boardId?: string;
  onClose: () => void;
  onSuccess?: (versionNumber: number | null | undefined) => void;
}

const DESCRIPTION_MAX = 280;
/** Strict major.minor.patch — same regex the backend enforces. */
const SEMVER_RE = /^\d+\.\d+\.\d+$/;

export function UploadVersionDialog({
  open,
  projectName,
  organisationId,
  boardId,
  onClose,
  onSuccess,
}: UploadVersionDialogProps) {
  const [tool, setTool] = useState<SourceTool>("kicad");
  const [files, setFiles] = useState<SlotFiles>({});
  const [slotErrors, setSlotErrors] = useState<
    Partial<Record<SlotKey, string | null>>
  >({});
  const [description, setDescription] = useState("");
  const [version, setVersion] = useState("");
  const [error, setError] = useState<string | null>(null);

  const upload = useUploadProject();

  const trimmedVersion = version.trim();
  const versionInvalid =
    trimmedVersion.length > 0 && !SEMVER_RE.test(trimmedVersion);
  const uploadReady = requiredSlotsFilled(tool, files);
  const hasSlotErrors = Object.values(slotErrors).some(Boolean);

  const reset = () => {
    setTool("kicad");
    setFiles({});
    setSlotErrors({});
    setDescription("");
    setVersion("");
    setError(null);
  };

  const handleClose = () => {
    if (upload.isPending) return;
    reset();
    onClose();
  };

  const handleToolChange = (next: SourceTool) => {
    setTool(next);
    setFiles({});
    setSlotErrors({});
  };

  const handleFile = (key: SlotKey, file: File | null) => {
    if (file) {
      const slot = getTool(tool).slots.find((s) => s.key === key);
      setSlotErrors((prev) => ({
        ...prev,
        [key]: slot ? validateSlotFile(file, slot) : null,
      }));
    } else {
      setSlotErrors((prev) => ({ ...prev, [key]: null }));
    }
    setFiles((prev) => ({ ...prev, [key]: file }));
  };

  const handleSubmit = () => {
    if (!uploadReady || versionInvalid || hasSlotErrors) return;

    const fd = buildBoardUploadFormData({
      tool,
      projectName,
      boardId,
      organisationId,
      versionSemver: trimmedVersion,
      versionDescription: description,
      files,
    });

    upload.mutate(fd, {
      onSuccess: (data) => {
        const num = data.version_number;
        handleClose();
        onSuccess?.(num);
      },
      onError: (err: unknown) => {
        const msg = err instanceof Error ? err.message : "Upload failed";
        setError(
          msg.startsWith("[object")
            ? "Upload failed — check the files and try again."
            : msg,
        );
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      slotProps={{
        paper: { sx: { width: "100%", maxWidth: 500, m: { xs: 1, sm: 4 } } },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        Upload new version
        <IconButton
          onClick={handleClose}
          size="small"
          aria-label="Close"
          disabled={upload.isPending}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      {upload.isPending && <LinearProgress />}

      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Uploading a new version of <strong>{projectName}</strong>. Previous
          versions stay accessible in the timeline; byte-identical re-uploads
          are deduped.
        </Typography>

        <ToolUploadFields
          tool={tool}
          onToolChange={handleToolChange}
          files={files}
          onFileChange={handleFile}
          errors={slotErrors}
          disabled={upload.isPending}
        />

        <TextField
          size="small"
          label="Version (optional)"
          placeholder="e.g., 1.0.1"
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          fullWidth
          error={versionInvalid}
          helperText={
            versionInvalid
              ? "Use major.minor.patch (e.g. 1.0.1)."
              : "Leave empty to bump the previous version's patch."
          }
        />

        <TextField
          size="small"
          label="Description (optional)"
          placeholder="e.g., fixed VCC routing on the PSU rail"
          value={description}
          onChange={(e) =>
            setDescription(e.target.value.slice(0, DESCRIPTION_MAX))
          }
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          helperText={`${description.length}/${DESCRIPTION_MAX}`}
        />

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          size="small"
          disabled={upload.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          disabled={
            !uploadReady || versionInvalid || hasSlotErrors || upload.isPending
          }
          onClick={handleSubmit}
          startIcon={
            upload.isPending ? (
              <CircularProgress size={14} color="inherit" />
            ) : null
          }
        >
          {upload.isPending ? "Uploading…" : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
